import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  DataGridColumn,
  parseColumnMultiFilter,
  RowAction,
  SortState,
} from '../data-table/data-table.types';
import { UiDataTableComponent } from '../data-table/data-table.component';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import { UiFileItem, UiStoredFileItem } from '../../molecules/file-upload/file-upload.types';
import { UiFileUploadComponent } from '../../molecules/file-upload/file-upload.component';
import { UiFormSectionComponent } from '../../molecules/form-section/form-section.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiTextareaComponent } from '../../atoms/textarea/textarea.component';
import {
  UiEntityDocumentDrawerMode,
  UiEntityDocumentDrawerState,
  UiEntityDocumentRow,
  UiEntityDocumentSavedFile,
  UiEntityDocumentSavePayload,
  UiEntityDocumentStoredFileRemove,
} from './entity-documents.types';

const MULTI_FILTER_KEYS = new Set(['archivos']);

/**
 * Organismo: documentos asociados a una entidad.
 * Tabla y un mismo drawer para alta, consulta y edición.
 * Presentacional: no conoce dominio ni API; recibe filas y emite eventos.
 */
@Component({
  selector: 'ui-entity-documents',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiDataTableComponent,
    UiDrawerComponent,
    UiFormSectionComponent,
    UiInputComponent,
    UiFileUploadComponent,
    UiTextareaComponent,
  ],
  templateUrl: './entity-documents.component.html',
  styleUrl: './entity-documents.component.scss',
  host: { class: 'ui-entity-documents-host' },
})
export class UiEntityDocumentsComponent {
  readonly rows = input<readonly UiEntityDocumentRow[]>([]);
  readonly loading = input(false);
  readonly title = input('Documentos');
  /** Si es false, Guardar emite el payload y deja el drawer abierto. */
  readonly closeOnSave = input(true);
  /** En edición, muestra la papelera de los archivos ya guardados. */
  readonly storedRemovable = input(false);

  readonly open = model(false);
  readonly mode = model<UiEntityDocumentDrawerMode>('new');

  readonly documentSave = output<UiEntityDocumentSavePayload>();
  readonly storedFileRemove = output<UiEntityDocumentStoredFileRemove>();
  readonly actionClick = output<{ row: UiEntityDocumentRow; action: RowAction }>();
  readonly exportExcel = output<void>();

  private readonly sourceRows = signal<UiEntityDocumentRow[]>([]);
  protected readonly displayRows = signal<UiEntityDocumentRow[]>([]);
  protected readonly total = computed(() => this.displayRows().length);

  protected readonly docNombre = signal('');
  protected readonly docArchivos = signal<UiFileItem[]>([]);
  protected readonly docNotas = signal('');
  protected readonly savedFiles = signal<readonly UiEntityDocumentSavedFile[]>([]);
  private readonly documentId = signal<string | null>(null);

  protected readonly drawerTitle = computed(() => {
    if (this.mode() === 'view') return 'Ver documento';
    if (this.mode() === 'edit') return 'Editar documento';
    return 'Nuevo documento';
  });
  protected readonly fieldsLocked = computed(() => this.mode() === 'view');
  protected readonly canRemoveStoredFiles = computed(
    () => this.storedRemovable() && this.mode() === 'edit',
  );

  private activeColumnFilters: Record<string, string> = {};

  protected readonly actionItems: RowAction[] = [
    { key: 'ver', label: 'Ver' },
    { key: 'editar', label: 'Editar' },
    { key: 'eliminar', label: 'Eliminar', destructive: true },
  ];

  protected readonly columns = computed<DataGridColumn[]>(() => {
    const archivosFilterOptions = [
      ...new Set(this.sourceRows().map(r => r.archivos)),
    ]
      .sort((a, b) => a - b)
      .map(n => ({ id: `archivos-${n}`, label: String(n), value: String(n) }));

    return [
      { key: 'nombre', header: 'Nombre', type: 'string' },
      {
        key: 'archivos',
        header: 'Archivos',
        type: 'number',
        filterType: 'multiselect',
        filterOptions: archivosFilterOptions,
      },
      { key: 'notas', header: 'Notas', type: 'string' },
    ];
  });

  constructor() {
    effect(() => {
      const next = [...this.rows()];
      this.sourceRows.set(next);
      this.applyFilters();
    });
  }

  openDrawer(state: UiEntityDocumentDrawerState): void {
    const mode = state.mode;
    this.mode.set(mode);
    this.documentId.set(mode === 'new' ? null : (state.documentId ?? null));
    this.docNombre.set(mode === 'new' ? '' : (state.nombre ?? ''));
    this.docNotas.set(mode === 'new' ? '' : (state.notas ?? ''));
    this.docArchivos.set([]);
    this.savedFiles.set(mode === 'new' ? [] : [...(state.archivos ?? [])]);
    this.open.set(true);
  }

  forgetStoredFile(fileId: string): void {
    this.savedFiles.update(files => files.filter(file => file.id !== fileId));
  }

  protected onStoredFileRemove(file: UiStoredFileItem): void {
    const documentId = this.documentId();
    const saved = this.savedFiles().find(item => item.id === file.id);
    if (!documentId || !saved) return;
    this.storedFileRemove.emit({ documentId, file: saved });
  }

  protected onNew(): void {
    this.openDrawer({ mode: 'new' });
  }

  protected onSave(): void {
    if (this.mode() === 'view') return;
    this.documentSave.emit({
      mode: this.mode() === 'edit' ? 'edit' : 'new',
      documentId: this.documentId(),
      nombre: this.docNombre(),
      archivos: this.docArchivos(),
      notas: this.docNotas(),
    });
  }

  protected onExportExcel(): void {
    this.exportExcel.emit();
  }

  protected onSort(event: SortState): void {
    this.displayRows.update(rows =>
      [...rows].sort((a, b) => {
        const va = a[event.sortBy as keyof UiEntityDocumentRow] ?? '';
        const vb = b[event.sortBy as keyof UiEntityDocumentRow] ?? '';
        return event.order === 'asc'
          ? String(va).localeCompare(String(vb), 'es', { numeric: true })
          : String(vb).localeCompare(String(va), 'es', { numeric: true });
      }),
    );
  }

  protected onFilter(filters: Record<string, string>): void {
    this.activeColumnFilters = filters;
    this.applyFilters();
  }

  protected onActionClick(e: { row: unknown; action: RowAction }): void {
    this.actionClick.emit({
      row: e.row as UiEntityDocumentRow,
      action: e.action,
    });
  }

  private applyFilters(): void {
    const base = this.sourceRows();
    const result = base.filter(row =>
      Object.entries(this.activeColumnFilters).every(([key, val]) => {
        if (!val) return true;
        if (MULTI_FILTER_KEYS.has(key)) {
          const selected = parseColumnMultiFilter(val);
          return (
            !selected.length ||
            selected.includes(String(row[key as keyof UiEntityDocumentRow] ?? ''))
          );
        }
        return String(row[key as keyof UiEntityDocumentRow] ?? '')
          .toLowerCase()
          .includes(val.toLowerCase());
      }),
    );
    this.displayRows.set(result);
  }
}
